// SourceFormModal — create + edit modal for a CRM `LeadSource`.
//
// CREATE: takes `code` + `displayName`. On success the parent caller
//   receives the `LeadSourceSecretReveal` envelope and is responsible
//   for routing the plaintext secret into a one-time reveal modal —
//   this modal never displays the secret itself.
//
// EDIT: takes `displayName` (rename) + `enabled` (toggle). `code` is
//   read-only after creation per the locked backend contract.
//
// @aiInvariant the `dirty` flag tracks meaningful changes vs the
//   baseline. Empty `displayName` is rejected by the shared schema
//   (min length 1) so the Save button stays disabled on blank input.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreateLeadSourceInputSchema,
  LEAD_SOURCE_CODE_REGEX,
  type CreateLeadSourceInput,
  type LeadSource,
  type LeadSourceSecretReveal,
  type UpdateLeadSourceInput,
} from '@magimp/shared';
import { Button, EditorModal, Input } from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { useCreateLeadSource, usePatchLeadSource } from '../queries.js';

export interface SourceFormModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  source?: LeadSource | undefined;
  onClose: () => void;
  onCreated?: ((reveal: LeadSourceSecretReveal) => void) | undefined;
  onUpdated?: ((source: LeadSource) => void) | undefined;
}

export function SourceFormModal({
  open,
  mode,
  source,
  onClose,
  onCreated,
  onUpdated,
}: SourceFormModalProps) {
  const { t } = useTranslation(['sources', 'common']);
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (mode === 'create') {
      setCode('');
      setDisplayName('');
      setEnabled(true);
    } else if (source) {
      setCode(source.code);
      setDisplayName(source.displayName);
      setEnabled(source.enabled);
    }
    setError(null);
  }, [open, mode, source]);

  const createMut = useCreateLeadSource();
  const patchMut = usePatchLeadSource(source?.code ?? '');

  const codeShapeOk = mode === 'edit' || LEAD_SOURCE_CODE_REGEX.test(code);
  const baseline = useMemo(
    () => ({
      code: source?.code ?? '',
      displayName: source?.displayName ?? '',
      enabled: source?.enabled ?? true,
    }),
    [source],
  );
  const dirty =
    mode === 'create'
      ? code.length > 0 || displayName.length > 0
      : displayName !== baseline.displayName || enabled !== baseline.enabled;

  const canSubmit =
    mode === 'create'
      ? codeShapeOk && displayName.trim().length > 0 && !createMut.isPending
      : dirty && displayName.trim().length > 0 && !patchMut.isPending;

  async function onSubmit(): Promise<void> {
    setError(null);
    try {
      if (mode === 'create') {
        const input: CreateLeadSourceInput = CreateLeadSourceInputSchema.parse({
          code,
          displayName: displayName.trim(),
        });
        const reveal = await createMut.mutateAsync(input);
        onCreated?.(reveal);
        onClose();
      } else if (source) {
        const body: UpdateLeadSourceInput = {};
        if (displayName.trim() !== baseline.displayName) body.displayName = displayName.trim();
        if (enabled !== baseline.enabled) body.enabled = enabled;
        const updated = await patchMut.mutateAsync(body);
        onUpdated?.(updated);
        onClose();
      }
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <EditorModal
      open={open}
      title={mode === 'create' ? t('sources:form.createTitle') : t('sources:form.editTitle')}
      dirty={dirty}
      busy={createMut.isPending || patchMut.isPending}
      onConfirmClose={onClose}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
          <Button
            kind="primary"
            disabled={!canSubmit}
            onClick={() => {
              void onSubmit();
            }}
            data-testid="source-form-submit"
          >
            {mode === 'create' ? t('sources:form.create') : t('sources:form.save')}
          </Button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>{t('sources:form.code')}</span>
          {mode === 'edit' ? (
            <span
              data-testid="source-code-readonly"
              style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--fg-1)' }}
            >
              {code}
            </span>
          ) : (
            <div data-testid="source-code-input">
              <Input
                value={code}
                onChange={(v) => setCode(v.toLowerCase())}
                maxLength={64}
                placeholder={t('sources:form.codePlaceholder')}
              />
            </div>
          )}
          {mode === 'create' && !codeShapeOk && code.length > 0 && (
            <span style={{ fontSize: 11, color: 'var(--status-error-fg)' }}>
              {t('sources:form.codeShapeError')}
            </span>
          )}
          {mode === 'create' && (
            <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{t('sources:form.codeHelp')}</span>
          )}
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>
            {t('sources:form.displayName')}
          </span>
          <div data-testid="source-display-name-input">
            <Input value={displayName} onChange={setDisplayName} maxLength={128} />
          </div>
        </label>
        {mode === 'edit' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              data-testid="source-enabled-checkbox"
            />
            <span>{t('sources:form.enabled')}</span>
          </label>
        )}
        {error && (
          <span
            role="alert"
            style={{ fontSize: 12, color: 'var(--status-error-fg)' }}
            data-testid="source-form-error"
          >
            {error}
          </span>
        )}
      </div>
    </EditorModal>
  );
}
